// Auth/role helpers for the admin app.
//
// The logged-in user (including their role) is persisted in localStorage under
// "user" at sign-in — see src/pages/Authentication/src/components/Forms/SignIn.jsx.
// Role names can arrive hyphenated ("super-admin") or underscored ("super_admin")
// depending on the backend payload, so every comparison goes through
// normalizeRole to avoid a separator mismatch silently locking out a valid user.
//
// This is defence-in-depth on the UI only. The backend (mohtaj-api) enforces the
// same role gate server-side — see the MOH-394 backend split.

export const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const normalizeRole = (role) =>
  String(role || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

// Role may be a plain string ("super-admin"), an array of them (the shape the
// login endpoint actually returns: role: ["super-admin"]), an object
// ({ name }/{ slug }), or live on a `roles` array. Collect every role name we
// can find, normalised.
//
// The array case matters: `typeof [] === "object"`, so without an explicit
// check an array falls into the object branch, yields no .name/.slug/.role, and
// contributes an empty string that filter(Boolean) then drops — leaving the
// user with zero roles and a 403 on every guarded route (MOH-703).
export const getUserRoles = (user = getCurrentUser()) => {
  if (!user) return [];
  const raw = [];
  const pushRole = (r) => {
    if (!r) return;
    if (Array.isArray(r)) {
      r.forEach(pushRole);
      return;
    }
    if (typeof r === "string") raw.push(r);
    else if (typeof r === "object") raw.push(r.name || r.slug || r.role || "");
  };
  pushRole(user.role);
  pushRole(user.roles);
  return raw.map(normalizeRole).filter(Boolean);
};

// True when the user holds at least one of the required roles. An empty/omitted
// requirement means "any authenticated user".
export const hasRole = (required, user = getCurrentUser()) => {
  const requiredList = (Array.isArray(required) ? required : [required])
    .map(normalizeRole)
    .filter(Boolean);
  if (requiredList.length === 0) return true;
  const roles = getUserRoles(user);
  return requiredList.some((r) => roles.includes(r));
};

export const isSuperAdmin = (user = getCurrentUser()) =>
  hasRole("super_admin", user);

export const SUPER_ADMIN_ONLY = ["super_admin"];
